import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';

export interface EnmonMeterPhotoLink {
  fileId: string;
  label: string;
  link: string;
}

interface EnmonMeterFilesClientInput {
  axiosClient?: InventoryAxiosClient;
}

export class EnmonMeterFilesClient {
  private readonly axios: InventoryAxiosClient;

  constructor(input: EnmonMeterFilesClientInput = {}) {
    this.axios = input.axiosClient ?? createInventoryAxiosClient();
  }

  async loadMeterPhotoLinks(input: { locationApiKey: string; meterId: string }) {
    if (!isMongoObjectId(input.meterId)) {
      return [];
    }

    const axios = createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey: input.locationApiKey,
    });
    const filesResponse = await axios.get<unknown>('/enmon-tools/files', {
      params: {
        collections: ['meters'],
        meters: [input.meterId],
      },
    });
    const files = readFiles(filesResponse.data);

    return Promise.all(
      files.map(async file => {
        const linkResponse = await axios.get<unknown>(`/enmon-tools/files/${encodeURIComponent(file.id)}/link`);

        return {
          fileId: file.id,
          label: file.label,
          link: readLink(linkResponse.data),
        };
      }),
    ).then(links => links.filter((link): link is EnmonMeterPhotoLink => Boolean(link.link)));
  }
}

export function isMongoObjectId(value: string | undefined) {
  return typeof value === 'string' && /^[a-f\d]{24}$/i.test(value.trim());
}

function readFiles(response: unknown) {
  const object = response as { result?: unknown[] };
  const result = Array.isArray(object?.result) ? object.result : Array.isArray(response) ? response : [];

  return result.flatMap(file => {
    const record = file as { _id?: string; filename?: string; id?: string; name?: string; originalname?: string };
    const id = record.id ?? record._id;

    if (!id) {
      return [];
    }

    return [
      {
        id,
        label: record.name ?? record.filename ?? record.originalname ?? 'Uploaded photo',
      },
    ];
  });
}

function readLink(response: unknown) {
  return (response as { link?: string }).link;
}
